import React from "react";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import CaseStudiesCard from "../CaseStudiesCard";
import Stack from "../../../Stack";
import Box from "../../../Box";
import Image from "../../../Image";
import UserTesting from "../../../../img/casestudy/design-sprint/user-testing.png";
import Mindmap from "../../../../img/casestudy/design-sprint/mind-map.jpg";
import ResearchPlan from "../../../../img/casestudy/design-sprint/research-plan.png";
import ExperienceMap from "../../../../img/casestudy/design-sprint/experience-map.jpg";
import Workshop from "../../../../img/casestudy/design-sprint/workshop.png";
import BlogLayout from "../BlogLayout";

const UserResearch = (
  <CaseStudiesCard id="research">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Walk-through of user research techniques applied during my time at MYOB.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout>
      <Stack spacing="var(--spacing-xx-large)">
        <Box>
          <Heading variant="larger">User Research</Heading>
          <p>
            During my five years at MYOB, I applied a range of discovery and user testing techniques across multiple
            product streams to guide design and product decisions. We worked lean, self-sourcing participants, using
            internal networks, and running quick, focused research cycles that informed sprints and validated
            hypotheses.
          </p>
        </Box>

        <Stack disableMargin spacing="96px">
          <Box margin="0">
            <Heading mb="small" variant="large">
              Sourcing Participants
            </Heading>
            <p style={{ maxWidth: "68ch" }}>
              Finding the right users was a challenge, as MYOB did not have a centralised research panel. We targeted
              small business owners, bookkeepers, and practicing accountants by:
            </p>
            <ul>
              <li>Using internal tools to identify and contact potential participants</li>
              <li>Exploring community message boards and user forums</li>
              <li>Reaching out through internal channels to locate customers matching key personas</li>
            </ul>
            <p>
              This approach consistently met participant quotas and provided direct access to diverse perspectives and
              real-world experiences.
            </p>
          </Box>

          <Box margin="0">
            <Heading mb="small" variant="large">
              Who Took Part
            </Heading>
            <p style={{ maxWidth: "68ch" }}>
              User research was a team effort. Engineers, BAs, and designers often joined sessions as observers, took
              notes, or asked follow-up questions. The engineering team was highly engaged and attentive, spotting
              opportunities to improve the user experience and contribute to design discussions. This approach built
              empathy across the team and made insights feel shared rather than handed down.
            </p>
          </Box>

          <Box margin="0">
            <Heading mb="small" variant="large">
              User Testing
            </Heading>

            <p style={{ maxWidth: "68ch", marginBottom: "var(--spacing-x-medium)" }}>
              We used clickable prototypes or journey maps to walk users through flows and features, observing
              interactions and capturing real-time feedback. Sessions lasted 30–60 minutes, in person or remotely, with
              observers taking notes directly on a user testing canvas. Artifacts such as testing scripts and group
              notes helped document findings, share insights, and validate hypotheses before development.
            </p>

            <Image dark src={UserTesting} alt="User Testing" caption="Testing Script & Themes" />
          </Box>

          <Box margin="0">
            <Heading mb="small" variant="large">
              User Discovery
            </Heading>
            <p style={{ maxWidth: "68ch" }}>
              Discovery sessions focused on understanding users’ behaviours, goals, pain points, and day-to-day context.
              Sessions lasted about an hour and followed a guided conversation that left room for natural storytelling.
              We spoke with as many users as possible, making discovery an ongoing process that continued even during
              hypothesis testing.
            </p>
            <p style={{ maxWidth: "68ch", marginBottom: "var(--spacing-x-medium)" }}>
              At the start of each discovery phase, I used mind maps to create the interview script. This helped me
              tease out areas of interest, structure questions, and identify patterns to explore during the sessions.
            </p>
            <Image
              dark
              src={Mindmap}
              alt="Mind map used to develop the user interview script"
              caption="Mind map used to develop the user interview script"
            />
          </Box>

          <Box margin="0">
            <Heading mb="small" variant="large">
              Synthesising Insights
            </Heading>
            <p style={{ maxWidth: "68ch" }}>
              After each round of user testing sessions, we collected notes and observations, which were distilled into
              visual artefacts highlighting key findings, recurring patterns, and user pain points. The team reviewed
              observations together, grouped findings into themes, and mapped them visually using whiteboards,
              spreadsheets, or journey maps. This process revealed friction points, clarified behaviours, and guided
              informed design decisions.
            </p>

            <Image dark src={ResearchPlan} alt="Validation Plan Canvas" caption="Validation Plan Canvas" />
            <p style={{ maxWidth: "68ch", marginTop: "var(--spacing-x-medium)" }}>
              The images illustrate the process and outcomes, from early workshop whiteboards to final journey maps,
              making insights visible for the team and enabling shared understanding across disciplines.
            </p>

            <Image dark src={ExperienceMap} alt="Customer Journey Map" caption="Customer Journey Map" />
          </Box>

          <Box margin="0">
            <Heading mb="small" variant="large">
              Outcome
            </Heading>

            <p>
              By running inclusive user testing and discovery sessions, I helped foster a collaborative environment
              where the whole team could engage with users and build empathy. Even without perfect tooling or a formal
              research panel, this lightweight approach allowed me to uncover meaningful insights, encourage iteration,
              and shape products that better reflected user needs.
            </p>
            <p style={{ maxWidth: "68ch" }}>
              <strong>Key results included</strong>
            </p>
            <ul style={{ marginBottom: "var(--spacing-x-medium)" }}>
              <li>
                Maintaining momentum between discovery and delivery, ensuring research directly informed each iteration
              </li>
              <li>
                Building stronger empathy across teams, as engineers, BAs, and designers observed user frustrations and
                successes firsthand
              </li>
              <li>
                Continuously validating ideas, not just designs, so decisions were grounded in real-world user behaviour
              </li>
            </ul>
            <Image dark src={Workshop} alt="Workshop" caption="Workshop" />
          </Box>
        </Stack>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default UserResearch;
